import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL, APP_URL } from '../../utils';

const fileUrl = (path, version) => {
  if (!path) return '';
  const stamp = version ? `?v=${encodeURIComponent(version)}` : '';
  return `${APP_URL}/${path}${stamp}`;
};

export default function ReglagesPaie() {
  const [settings, setSettings] = useState(null);
  const [name, setName] = useState('');
  const [signature, setSignature] = useState(null);
  const [cachet, setCachet] = useState(null);
  const [removeSignature, setRemoveSignature] = useState(false);
  const [removeCachet, setRemoveCachet] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const apply = (data) => {
    setSettings(data);
    setName(data?.signataire_nom || '');
    setSignature(null);
    setCachet(null);
    setRemoveSignature(false);
    setRemoveCachet(false);
  };

  useEffect(() => {
    const load = async () => {
      setError('');
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${APP_API_URL}/payroll-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        apply(response.data.settings);
      } catch (err) {
        setError('Impossible de charger la signature.');
      }
    };
    load();
  }, []);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const token = localStorage.getItem('token');
      const body = new FormData();
      body.append('signataire_nom', name);
      if (signature) body.append('signature', signature);
      if (cachet) body.append('cachet', cachet);
      if (removeSignature) body.append('remove_signature', '1');
      if (removeCachet) body.append('remove_cachet', '1');
      const response = await axios.post(`${APP_API_URL}/payroll-settings`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      apply(response.data.settings);
      setMessage('Signature et cachet enregistrés. Ils apparaissent sur les fiches de paie.');
    } catch (err) {
      setError(err.response?.data?.message || 'Enregistrement impossible. Utilisez une image JPG, PNG ou WEBP de moins de 2 Mo.');
    } finally {
      setSaving(false);
    }
  };

  const signaturePreview = useMemo(() => {
    if (signature) return URL.createObjectURL(signature);
    if (removeSignature) return '';
    return fileUrl(settings?.signature_path, settings?.updated_at);
  }, [signature, removeSignature, settings]);
  const cachetPreview = useMemo(() => {
    if (cachet) return URL.createObjectURL(cachet);
    if (removeCachet) return '';
    return fileUrl(settings?.cachet_path, settings?.updated_at);
  }, [cachet, removeCachet, settings]);

  return (
    <MainLayout>
      <div className="duo-page">
        <div className="d-flex gap-2 mb-3">
          <Link to="/personnel/paie" className="duo-btn duo-btn--ghost">Paie du mois</Link>
        </div>
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Ressources humaines</p>
          <h1 className="duo-hero-title">Signature et cachet</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Ces éléments s&apos;impriment sur chaque fiche de paie, suivis du nom du responsable.
            Préparez un PNG à fond transparent.
          </p>
        </div>
        {error && <p className="text-danger">{error}</p>}
        {message && <p className="text-success">{message}</p>}
        <form onSubmit={save} className="duo-panel duo-panel--form">
          <label>
            Nom du responsable
            <input type="text" className="form-control" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom et prénom" />
          </label>
          <div className="duo-form-row duo-form-row--2">
            <label>
              Signature — 600 × 200 px
              <input type="file" accept="image/png,image/jpeg,image/webp" className="form-control" onChange={(e) => { setSignature(e.target.files?.[0] || null); setRemoveSignature(false); }} />
              <span className="text-muted small d-block mt-1">Image horizontale, 600 pixels de large et 200 de haut. Elle s&apos;imprime en 160 × 54 px. PNG transparent, 2 Mo maximum.</span>
            </label>
            <label>
              Cachet — 400 × 400 px
              <input type="file" accept="image/png,image/jpeg,image/webp" className="form-control" onChange={(e) => { setCachet(e.target.files?.[0] || null); setRemoveCachet(false); }} />
              <span className="text-muted small d-block mt-1">Image carrée, 400 × 400 pixels. Elle s&apos;imprime en 90 × 90 px. PNG transparent, 2 Mo maximum.</span>
            </label>
          </div>
          <div className="d-flex flex-wrap gap-4 mt-2">
            <div>
              <p className="mb-1 fw-bold">Aperçu signature (160 × 54)</p>
              {signaturePreview ? (
                <img src={signaturePreview} alt="Signature" style={{ width: 160, height: 54, objectFit: 'contain', border: '1px dashed #9ca3af', background: '#fff' }} />
              ) : <p className="text-muted mb-0">Aucune signature</p>}
              {settings?.signature_path && !removeSignature && (
                <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm mt-2" onClick={() => { setRemoveSignature(true); setSignature(null); }}>
                  Retirer la signature
                </button>
              )}
            </div>
            <div>
              <p className="mb-1 fw-bold">Aperçu cachet (90 × 90)</p>
              {cachetPreview ? (
                <img src={cachetPreview} alt="Cachet" style={{ width: 90, height: 90, objectFit: 'contain', border: '1px dashed #9ca3af', background: '#fff' }} />
              ) : <p className="text-muted mb-0">Aucun cachet</p>}
              {settings?.cachet_path && !removeCachet && (
                <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm mt-2" onClick={() => { setRemoveCachet(true); setCachet(null); }}>
                  Retirer le cachet
                </button>
              )}
            </div>
          </div>
          <button type="submit" className="duo-btn duo-btn--primary mt-3" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </form>
      </div>
    </MainLayout>
  );
}
